import React, { useMemo } from 'react';
import dayjs from 'dayjs';

import { RAW_DATE_FORMAT } from '@/common/constants';
import { TransactionData } from '@/modules/transaction/transaction.types';

import OffsetRepayment from './OffsetRepayment/OffsetRepayment';
import PreFormattedMessage from './PreFormattedMessage/PreFormattedMessage';

interface Message {
  date: string;
  message: React.ReactNode;
}

const formattedDate = (date: string) => dayjs(date).format(RAW_DATE_FORMAT);

interface Props {
  data: TransactionData;
  employeeFirstName?: string;
  onUploadDocument?: () => void;
  onUploadLmnDocument?: () => void;
  isUploadButtonEnabled?: boolean;
}

const useClaimActivitiesMessages = (props: Props) => {
  const {
    data,
    onUploadDocument,
    onUploadLmnDocument,
    isUploadButtonEnabled,
  } = props;

  const preFormattedMessages = useMemo(
    () =>
      data?.v3ActivityDetails?.map((item) => ({
        date: item.date,
        message: (
          <PreFormattedMessage
            preFormattedData={item}
            onUploadDocument={onUploadDocument}
            onUploadLmnDocument={onUploadLmnDocument}
            data={data}
            isUploadButtonEnabled={isUploadButtonEnabled}
          />
        ),
      })) || [],
    [data, onUploadDocument, onUploadLmnDocument, isUploadButtonEnabled]
  );

  const offsetRepayments = useMemo(
    () =>
      data?.claimOffsetDonors?.map((item) => ({
        date: item.date,
        message: <OffsetRepayment data={item} />,
      })) || [],
    [data]
  );

  const allActivitiesCount = useMemo(
    () => preFormattedMessages?.length + offsetRepayments?.length,
    [preFormattedMessages, offsetRepayments]
  );

  const dates = useMemo(
    () =>
      [
        ...new Set([
          ...new Set(preFormattedMessages?.map((item) => formattedDate(item.date))),
          ...new Set(offsetRepayments?.map((item) => formattedDate(item.date))),
        ]),
      ].sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf()),
    [preFormattedMessages, offsetRepayments]
  );

  const messagesSortedByDate = useMemo(
    () =>
      dates.map((date) => ({
        date,
        messages: (
          [
            ...(offsetRepayments?.filter((item) => formattedDate(item.date) === date) || []),
            ...(preFormattedMessages?.filter((item) => formattedDate(item.date) === date) || []),
          ] as Message[]
        ).sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()),
      })),
    [dates, preFormattedMessages, offsetRepayments]
  );

  return { dates, messagesSortedByDate, allActivitiesCount };
};

export default useClaimActivitiesMessages;
